import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import Login from './pages/auth/Login';
import ForgotPassword from './pages/auth/ForgotPassword';
import ForcePasswordChange from './pages/auth/ForcePasswordChange';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import PermitList from './pages/permits/PermitList';
import PermitCreate from './pages/permits/PermitCreate';
import PermitView from './pages/permits/PermitView';
import PermitEdit from './pages/permits/PermitEdit';
import EditRequestCreate from './pages/permits/EditRequestCreate';
import EditRequestView from './pages/permits/EditRequestView';
import CancellationRequestCreate from './pages/permits/CancellationRequestCreate';
import CancellationRequestView from './pages/permits/CancellationRequestView';
import CancellationRequestsList from './pages/cancellationRequests/CancellationRequestsList';
import EditRequestsList from './pages/editRequests/EditRequestsList';
import WorkflowDashboard from './pages/workflow/WorkflowDashboard';
import UserManagement from './pages/admin/UserManagement';
import SubdivisionManagement from './pages/admin/SubdivisionManagement';
import Reports from './pages/reports/Reports';
import PublicSearch from './pages/public/PublicSearch';
import PublicCirculars from './pages/public/PublicCirculars';
import Home from './pages/Home';
import AnnouncementsDashboard from './pages/announcements/AnnouncementsDashboard';
import AnnouncementCreate from './pages/announcements/AnnouncementCreate';

function App() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/search" element={<PublicSearch />} />
        <Route path="/circulars" element={<PublicCirculars />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  // Force password change if required
  if (user.requirePasswordChange) {
    return <ForcePasswordChange />;
  }

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/dashboard" element={<Dashboard />} />

        {/* Profile Route - All authenticated users */}
        <Route path="/profile" element={<Profile />} />

        {/* Permit Routes */}
        <Route path="/permits" element={<PermitList />} />
        <Route path="/permits/new" element={<PermitCreate />} />
        <Route path="/permits/:id" element={<PermitView />} />
        <Route path="/permits/:id/edit" element={<PermitEdit />} />
        <Route path="/permits/:id/request-edit" element={<EditRequestCreate />} />

        {/* Edit Request Routes */}
        <Route path="/edit-requests" element={<EditRequestsList />} />
        <Route path="/edit-requests/:id" element={<EditRequestView />} />

        {/* Cancellation Request Routes */}
        <Route path="/permits/:id/cancellation-request/create" element={<CancellationRequestCreate />} />
        <Route path="/cancellation-requests/:requestId" element={<CancellationRequestView />} />
        {(['bo', 'eac', 'sdo', 'adc', 'dc'].includes(user.role)) && (
          <Route path="/cancellation-requests" element={<CancellationRequestsList />} />
        )}

        {/* Workflow Routes - All roles */}
        {(['bo', 'eac', 'sdo', 'adc', 'dc'].includes(user.role)) && (
          <Route path="/workflow" element={<WorkflowDashboard />} />
        )}

        {/* User Management - DC and ADC (ADC with subdivision restrictions) */}
        {['dc', 'adc'].includes(user.role) && (
          <Route path="/users" element={<UserManagement />} />
        )}

        {/* Admin Routes - DC only */}
        {user.role === 'dc' && (
          <Route path="/admin/subdivisions" element={<SubdivisionManagement />} />
        )}

        {/* Reports - Reviewers only (EAC, SDO, ADC, DC) */}
        {(['eac', 'sdo', 'adc', 'dc'].includes(user.role)) && (
          <Route path="/reports" element={<Reports />} />
        )}
        
        {/* Announcements / Circulars */}
        <Route path="/announcements" element={<AnnouncementsDashboard />} />
        <Route path="/announcements/new" element={<AnnouncementCreate />} />

        {/* Public Search & Public Circulars */}
        <Route path="/search" element={<PublicSearch />} />
        <Route path="/circulars" element={<PublicCirculars />} />
        
        {/* Fallback */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Layout>
  );
}

export default App;